import { useForm } from "react-hook-form";
import { Link, useSearch } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import { betterAuthErrorText } from "lib/auth/authClient";
import { confirmPasswordRules, newPasswordFieldRules } from "lib/auth/passwordRules";
import { INVALID_RESET_TOKEN_CODE, useResetPassword } from "lib/hooks/api/auth/useResetPassword";
import { Card, Field, Form, Page } from "./authPageStyles";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { StotlesLogo } from "./StotlesLogo";

type FormValues = {
  password: string;
  confirmPassword: string;
};

/**
 * Shown when the emailed link can't be used: no token in the URL, or better-auth rejected it. We
 * can't tell those apart, so the copy names the likeliest cause — expiry — and the other reasons.
 * The user is signed out here, so "request a new one" goes to the public forgot-password page
 * rather than the login-gated account settings.
 */
function UnusableLinkCard() {
  return (
    <Page>
      <Card centred>
        <StotlesLogo />
        <Text h1 color="sysDestructiveDefault">
          This password reset link has expired
        </Text>
        <Text color="sysTextSecondary">
          Reset links expire one hour after they&apos;re sent, and can only be used once.{" "}
          <Link to="/auth/forgot-password">Request a new one.</Link>
        </Text>
        <Text color="sysTextSecondary">
          <Link to="/auth/login">Back to log in</Link>
        </Text>
      </Card>
    </Page>
  );
}

/**
 * Landing page for the emailed password-reset link (see ChangePassword + the auth-service
 * sendResetPassword hook). better-auth validates the token and redirects here with `?token=…`; the
 * user sets a new password, which we submit to better-auth's reset-password endpoint.
 * @returns
 */
export function BetterAuthResetPasswordPage() {
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    // Validate on change so `isValid` (which gates the submit button) stays live and the
    // confirm-password match is checked without any manual trigger wiring.
    mode: "onChange",
    defaultValues: { password: "", confirmPassword: "" },
  });

  const password = watch("password");

  // `strict: false` lets `token` resolve to undefined when rendered outside its route (e.g. tests).
  const { token } = useSearch({ strict: false });

  const { mutate: resetPassword, isSuccess, isError, error, isLoading } = useResetPassword();

  // The token is only checked on submit, so a dead link fails here rather than on page load.
  if (!token || error?.code === INVALID_RESET_TOKEN_CODE) {
    return <UnusableLinkCard />;
  }

  if (isSuccess) {
    return (
      <Page>
        <Card centred>
          <StotlesLogo />
          <Text h1>Password updated</Text>
          <Text color="sysTextSecondary">
            Your password has been changed. <Link to="/auth/login">Log in</Link> with your new
            password.
          </Text>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <StotlesLogo />
        <Text h1>Reset your password</Text>
        <Form
          onSubmit={handleSubmit(({ password }) => resetPassword({ newPassword: password, token }))}
        >
          <Field>
            <Label htmlFor="password">New password</Label>
            <Input
              name="password"
              control={control}
              type="password"
              autoComplete="new-password"
              placeholder="Enter a new password"
              rules={{ ...newPasswordFieldRules, deps: ["confirmPassword"] }}
            />
            {errors.password?.message && (
              <Text color="sysDestructiveDefault">{errors.password.message}</Text>
            )}
          </Field>
          <PasswordStrengthMeter password={password} />
          <Field>
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input
              name="confirmPassword"
              control={control}
              type="password"
              autoComplete="new-password"
              placeholder="Re-enter your new password"
              rules={confirmPasswordRules(() => getValues("password"))}
            />
            {errors.confirmPassword?.message && (
              <Text color="sysDestructiveDefault">{errors.confirmPassword.message}</Text>
            )}
          </Field>

          {isError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "Could not reset password")}
            </Text>
          )}

          <Button
            buttonType="primary"
            type="submit"
            fullWidth
            loading={isLoading}
            disabled={!isValid}
          >
            Reset password
          </Button>
        </Form>
      </Card>
    </Page>
  );
}
